import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { useAuth } from '@/hooks/useAuth'
import { getShipmentsForCustomer, getCustomerStats } from '@/services/firebase'
import { Shipment } from '@/types'
import LoadingSpinner from '@/components/LoadingSpinner'
import { 
  TruckIcon, 
  DocumentTextIcon, 
  ClockIcon, 
  CheckCircleIcon,
  PlusIcon,
  ArrowRightIcon
} from '@heroicons/react/24/outline'

// Define proper types for stats
interface CustomerStats {
  totalShipments: number
  activeShipments: number
  deliveredShipments: number
  totalInvoices: number
}

export default function CustomerDashboard() {
  const { user } = useAuth()
  const [recentShipments, setRecentShipments] = useState<Shipment[]>([])
  const [stats, setStats] = useState<CustomerStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Validate user object
  const isValidUser = user && user.id && user.name && typeof user.id === 'string' && typeof user.name === 'string'

  useEffect(() => {
    if (!isValidUser) {
      setLoading(false)
      return
    }

    const fetchDashboardData = async () => {
      try {
        setLoading(true)
        setError(null)
        
        const [shipmentsData, statsData] = await Promise.all([
          getShipmentsForCustomer(user.id, 5), // Get last 5 shipments
          getCustomerStats(user.id)
        ])
        
        // Validate data before setting state
        if (Array.isArray(shipmentsData)) {
          setRecentShipments(shipmentsData)
        }
        
        if (statsData && typeof statsData === 'object') {
          setStats({
            totalShipments: Number(statsData.totalShipments) || 0,
            activeShipments: Number(statsData.activeShipments) || 0,
            deliveredShipments: Number(statsData.deliveredShipments) || 0,
            totalInvoices: Number(statsData.totalInvoices) || 0
          })
        }
      } catch (error) {
        // Log error internally but don't expose details to console
        setError('Unable to load dashboard data. Please try again later.')
        // In production, you might want to send this to an error reporting service
        // but never log sensitive information to console
      } finally {
        setLoading(false)
      }
    }

    fetchDashboardData()
  }, [user, isValidUser])

  // Helper function to get status step for progress indicator
  const getStatusStep = (status: string) => {
    switch (status) {
      case 'pending': return 1
      case 'in_transit': return 2
      case 'delivered': return 3
      default: return 1
    }
  }

  // Helper function to format ETA
  const formatETA = (shipment: Shipment) => {
    if (shipment.estimatedDeliveryDate) {
      return new Date(shipment.estimatedDeliveryDate).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric'
      })
    }
    return 'TBD'
  }

  // Helper function to get destination string
  const getDestinationString = (shipment: Shipment) => {
    const city = shipment.destination?.city || 'Unknown City'
    const country = shipment.destination?.country || 'Unknown Country'
    return `${city}, ${country}`
  }

  // Show error state
  if (error) {
    return (
      <div className="flex flex-col justify-center items-center h-64 p-6">
        <div className="text-center">
          <div className="text-red-500 text-lg font-medium mb-2">Error</div>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => window.location.reload()} 
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // Show loading state
  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <LoadingSpinner />
      </div>
    )
  }

  // Show unauthorized state
  if (!isValidUser) {
    return (
      <div className="flex flex-col justify-center items-center h-64 p-6">
        <div className="text-center">
          <div className="text-red-500 text-lg font-medium mb-2">Access Denied</div>
          <p className="text-gray-600 mb-4">You must be logged in to view this dashboard.</p>
          <Link to="/login" className="btn-primary">
            Go to Login
          </Link>
        </div>
      </div>
    )
  }

  const statCards = [
    { 
      name: 'Total Shipments', 
      value: stats?.totalShipments || 0, 
      icon: TruckIcon,
      color: 'bg-blue-500'
    },
    { 
      name: 'Active Shipments', 
      value: stats?.activeShipments || 0, 
      icon: ClockIcon,
      color: 'bg-yellow-500'
    },
    { 
      name: 'Delivered', 
      value: stats?.deliveredShipments || 0, 
      icon: CheckCircleIcon,
      color: 'bg-green-500'
    },
    { 
      name: 'Total Invoices', 
      value: stats?.totalInvoices || 0, 
      icon: DocumentTextIcon,
      color: 'bg-purple-500'
    },
  ]

  return (
    <div className="p-4 sm:p-6 bg-gray-50 min-h-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center mb-6 space-y-4 sm:space-y-0">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Hi, {user.name}!</h1>
          <p className="text-sm text-gray-500 mt-1">Here's an overview of your shipping activity</p>
        </div>
        <Link 
          to="/portal/shipments/new" 
          className="btn-primary inline-flex items-center w-full sm:w-auto justify-center"
        >
          <PlusIcon className="h-5 w-5 mr-2" />
          New Shipment
        </Link>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 gap-4 sm:gap-5 sm:grid-cols-2 lg:grid-cols-4 mb-6">
        {statCards.map((item) => (
          <div key={item.name} className="bg-white overflow-hidden shadow rounded-lg">
            <div className="p-4 sm:p-5">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <div className={`p-2 sm:p-3 rounded-md ${item.color}`}>
                    <item.icon className="h-5 w-5 sm:h-6 sm:w-6 text-white" aria-hidden="true" />
                  </div>
                </div>
                <div className="ml-3 sm:ml-5 w-0 flex-1">
                  <dl>
                    <dt className="text-xs sm:text-sm font-medium text-gray-500 truncate">{item.name}</dt>
                    <dd className="text-xl sm:text-2xl font-semibold text-gray-900">{item.value}</dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Recent Shipments */}
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
            <h3 className="text-lg font-semibold text-gray-900">Recent Shipments</h3>
            <Link to="/portal/shipments" className="text-sm font-medium text-blue-600 hover:text-blue-700">
              View All Shipments
            </Link>
          </div>
        </div>
        
        {recentShipments.length === 0 ? (
          <div className="p-6 text-center">
            <TruckIcon className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">No shipments yet</h3>
            <p className="mt-1 text-sm text-gray-500">Get started by creating your first shipment.</p>
            <div className="mt-6">
              <Link 
                to="/portal/shipments/new" 
                className="btn-primary inline-flex items-center"
              >
                <PlusIcon className="h-5 w-5 mr-2" />
                Create Shipment
              </Link>
            </div>
          </div>
        ) : (
          <div className="divide-y divide-gray-200">
            {recentShipments.map((shipment) => {
              const statusStep = getStatusStep(shipment.status)
              const destination = getDestinationString(shipment)
              const eta = formatETA(shipment)
              
              return (
                <div key={shipment.id} className="p-4 sm:p-6">
                  <div className="space-y-4">
                    {/* Header with Tracking Number and Date */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-2 sm:space-y-0">
                      <Link 
                        to={`/portal/shipments/${shipment.id}`}
                        className="text-sm font-semibold text-blue-600 hover:text-blue-700"
                      >
                        #{shipment.trackingNumber}
                      </Link>
                      <p className="text-sm text-gray-500">
                        {new Date(shipment.createdAt).toLocaleDateString()}
                      </p>
                    </div>

                    {/* Destination and ETA */}
                    <div className="flex items-center space-x-2 text-sm text-gray-700">
                      <ArrowRightIcon className="h-4 w-4 text-gray-400" />
                      <span className="font-medium">{destination}</span>
                      <span className="text-gray-400">|</span>
                      <span>ETA: {eta}</span>
                    </div>

                    {/* Progress Indicator */}
                    <div className="flex items-center space-x-2">
                      <div className="flex items-center space-x-1">
                        <div className={`w-2 h-2 rounded-full ${statusStep >= 1 ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                        <span className={`text-xs ${statusStep >= 1 ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>Ordered</span>
                      </div>
                      <div className="flex-1 h-px bg-gray-300"></div>
                      <div className="flex items-center space-x-1">
                        <div className={`w-2 h-2 rounded-full ${statusStep >= 2 ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                        <span className={`text-xs ${statusStep >= 2 ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>In Transit</span>
                      </div>
                      <div className="flex-1 h-px bg-gray-300"></div>
                      <div className="flex items-center space-x-1">
                        <div className={`w-2 h-2 rounded-full ${statusStep >= 3 ? 'bg-blue-500' : 'bg-gray-300'}`}></div>
                        <span className={`text-xs ${statusStep >= 3 ? 'text-blue-600 font-medium' : 'text-gray-400'}`}>Delivered</span>
                      </div>
                    </div>

                    {/* Status Badge and View Details */}
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between space-y-3 sm:space-y-0">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold capitalize w-fit ${
                        shipment.status === 'delivered' ? 'bg-green-100 text-green-800' :
                        shipment.status === 'in_transit' ? 'bg-blue-100 text-blue-800' :
                        shipment.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                        'bg-gray-100 text-gray-800'
                      }`}>
                        {shipment.status.replace('_', ' ')}
                      </span>
                      <Link 
                        to={`/portal/shipments/${shipment.id}`}
                        className="text-sm font-medium text-blue-600 hover:text-blue-700 inline-flex items-center"
                      >
                        View Details
                        <ArrowRightIcon className="h-4 w-4 ml-1" />
                      </Link>
                    </div>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
} 